/**
 * 系统管理员管理页面
 * 功能：管理员的增删改查、角色分配
 */

import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Input,
  Modal,
  Form,
  message,
  Popconfirm,
  Select,
  Card,
  Avatar,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  UserOutlined,
  ReloadOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { API_BASE_URL, API_ENDPOINTS } from '../../api/config';

interface AdminUserType {
  id: number;
  email: string;
  nickname?: string;
  avatar?: string;
  status: number;
  last_login_at?: string;
  created_at?: string;
  roles: { id: number; name: string; code: string }[];
}

interface RoleType {
  id: number;
  name: string;
  code: string;
}

const AdminUserManagement: React.FC = () => {
  const [users, setUsers] = useState<AdminUserType[]>([]);
  const [roles, setRoles] = useState<RoleType[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [keyword, setKeyword] = useState('');
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUserType | null>(null);
  const [form] = Form.useForm();
  const [addForm] = Form.useForm();

  // 获取 token
  const getToken = () => localStorage.getItem('token') || '';

  // 获取管理员列表
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
      });
      if (keyword) params.append('keyword', keyword);

      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.system_app.users}?${params}`,
        {
          headers: { Authorization: `Bearer ${getToken()}` },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
        setTotal(data.total);
      } else {
        const error = await response.json();
        message.error(error.detail || '获取数据失败');
      }
    } catch (error) {
      message.error('网络错误');
    } finally {
      setLoading(false);
    }
  };

  // 获取角色列表
  const fetchRoles = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.system_app.roles}`,
        {
          headers: { Authorization: `Bearer ${getToken()}` },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setRoles(data.roles);
      }
    } catch (error) {
      console.error('获取角色列表失败', error);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, [page, pageSize]);

  // 编辑用户
  const handleEdit = (user: AdminUserType) => {
    setEditingUser(user);
    form.setFieldsValue({
      nickname: user.nickname,
      status: user.status,
      role_ids: user.roles.map((r) => r.id),
    });
    setEditModalVisible(true);
  };

  // 保存编辑
  const handleSaveEdit = async () => {
    try {
      const values = await form.validateFields();
      
      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.system_app.userDetail(editingUser!.id)}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify(values),
        }
      );

      if (response.ok) {
        message.success('更新成功');
        setEditModalVisible(false);
        fetchUsers();
      } else {
        const error = await response.json();
        message.error(error.detail || '更新失败');
      }
    } catch (error) {
      message.error('操作失败');
    }
  };

  // 删除用户
  const handleDelete = async (id: number) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.system_app.userDetail(id)}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${getToken()}` },
        }
      );

      if (response.ok) {
        message.success('删除成功');
        fetchUsers();
      } else {
        const error = await response.json();
        message.error(error.detail || '删除失败');
      }
    } catch (error) {
      message.error('操作失败');
    }
  };

  // 打开添加弹窗
  const handleAdd = () => {
    addForm.resetFields();
    setAddModalVisible(true);
  };

  // 创建管理员
  const handleCreate = async () => {
    try {
      const values = await addForm.validateFields();
      
      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.system_app.users}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify(values),
        }
      );

      if (response.ok) {
        message.success('创建成功');
        setAddModalVisible(false);
        fetchUsers();
      } else {
        const error = await response.json();
        message.error(error.detail || '创建失败');
      }
    } catch (error) {
      message.error('操作失败');
    }
  };

  // 表格列定义
  const columns: ColumnsType<AdminUserType> = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 60,
    },
    {
      title: '头像',
      dataIndex: 'avatar',
      width: 60,
      render: (avatar: string) => (
        <Avatar src={avatar} icon={<UserOutlined />} />
      ),
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      width: 200,
    },
    {
      title: '昵称',
      dataIndex: 'nickname',
      width: 120,
    },
    {
      title: '角色',
      dataIndex: 'roles',
      width: 200,
      render: (roles: RoleType[]) => (
        <Space wrap>
          {roles.map((role) => (
            <Tag
              key={role.id}
              color={role.code === 'super_admin' ? 'red' : 'blue'}
            >
              {role.name}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 80,
      render: (status: number) => (
        <Tag color={status === 1 ? 'green' : 'red'}>
          {status === 1 ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '最后登录',
      dataIndex: 'last_login_at',
      width: 160,
      render: (time: string) =>
        time ? new Date(time).toLocaleString('zh-CN') : '-',
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      width: 160,
      render: (time: string) =>
        time ? new Date(time).toLocaleString('zh-CN') : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除该管理员？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card title="🛡️ 系统管理员">
        {/* 搜索区 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <Space>
            <Input
              placeholder="搜索邮箱或昵称"
              prefix={<SearchOutlined />}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onPressEnter={fetchUsers}
              style={{ width: 200 }}
            />
            <Button type="primary" onClick={fetchUsers}>
              搜索
            </Button>
            <Button icon={<ReloadOutlined />} onClick={fetchUsers}>
              刷新
            </Button>
          </Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            新增管理员
          </Button>
        </div>

        {/* 表格 */}
        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* 编辑弹窗 */}
      <Modal
        title="编辑管理员"
        open={editModalVisible}
        onOk={handleSaveEdit}
        onCancel={() => setEditModalVisible(false)}
        okText="保存"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item label="邮箱">
            <Input value={editingUser?.email} disabled />
          </Form.Item>
          <Form.Item name="nickname" label="昵称">
            <Input placeholder="请输入昵称" />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select>
              <Select.Option value={1}>启用</Select.Option>
              <Select.Option value={0}>禁用</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="role_ids" label="角色">
            <Select mode="multiple" placeholder="选择角色">
              {roles.map((role) => (
                <Select.Option key={role.id} value={role.id}>
                  {role.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* 新增弹窗 */}
      <Modal
        title="新增管理员"
        open={addModalVisible}
        onOk={handleCreate}
        onCancel={() => setAddModalVisible(false)}
        okText="创建"
        cancelText="取消"
      >
        <Form form={addForm} layout="vertical">
          <Form.Item
            name="email"
            label="邮箱"
            rules={[{ required: true, message: '请输入邮箱' }, { type: 'email', message: '请输入有效的邮箱' }]}
          >
            <Input placeholder="请输入邮箱" />
          </Form.Item>
          <Form.Item name="nickname" label="昵称">
            <Input placeholder="请输入昵称（默认使用邮箱前缀）" />
          </Form.Item>
          <Form.Item name="role_ids" label="角色">
            <Select mode="multiple" placeholder="选择角色">
              {roles.map((role) => (
                <Select.Option key={role.id} value={role.id}>
                  {role.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <p style={{ color: '#999', fontSize: '12px', marginTop: '8px' }}>
            默认密码：邮箱前缀 + "123"（如邮箱为 test@example.com，密码为 test123）
          </p>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminUserManagement;
